'use client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import moment from 'moment-timezone';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router';
import {
  bookDeliverySlot,
  fetchAvailableSlots,
  sendSupportMessage,
  type AvailableSlot,
} from '../../api';
import { useWebsiteAppContext } from '../../hooks/useWebsiteAppContext';
import { ACTIVE_ORDER_STATUSES, PAST_ORDER_STATUSES } from '../../../miniapp/constants/orders';
import type { Order } from '../../../miniapp/types';
import { formatCurrency } from '../../utils/formatters';
import { getLocalizedApiErrorMessage } from '../../utils/errorHelpers';
import Icon from '../../../miniapp/ui-kit/Icon';

const ORDER_STATUS_LABELS: Record<string, string> = {
  PENDING: 'Awaiting Payment',
  PAYMENT_REVIEW: 'Payment Review',
  CONFIRMED: 'Confirmed',
  DONE: 'Delivered',
  CANCELLED: 'Cancelled',
};

const ORDER_STATUS_BADGE_CLASSES: Record<string, string> = {
  PENDING: 'bg-warning-container text-on-warning-container',
  PAYMENT_REVIEW: 'bg-accent-soft text-on-accent-soft',
  CONFIRMED: 'bg-success-container text-on-success-container',
  DONE: 'bg-success-container text-on-success-container',
  CANCELLED: 'bg-error-container text-on-error-container',
};

function formatOrderDate(value: string, locale: string): string {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(parsed);
}

function formatPaymentMethod(value: Order['paymentMethod'], t: TFunction): string {
  if (value === 'CASH_ON_DELIVERY') {
    return t('paymentMethod.cashOnDelivery');
  }
  return t('paymentMethod.ibanTransfer');
}

function getStatusLabel(status: string, t: TFunction): string {
  const fallback = ORDER_STATUS_LABELS[status] ?? status;
  return t(`orderStatus.${status}`, { defaultValue: fallback });
}

function getStatusBadgeClass(status: string): string {
  return ORDER_STATUS_BADGE_CLASSES[status] ?? 'bg-secondary text-text-secondary';
}

function getDeliverySlotLabel(
  order: Order,
  timezone: string | null | undefined,
  locale: string,
  t: TFunction,
): string | null {
  if (!order.deliverySlot) {
    return null;
  }

  const start = new Date(order.deliverySlot.startDateTime);
  const end = new Date(order.deliverySlot.endDateTime);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null;
  }

  const formatDate = new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(timezone ? { timeZone: timezone } : {}),
  });
  const formatTime = new Intl.DateTimeFormat(locale, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(timezone ? { timeZone: timezone } : {}),
  });

  return t('website.orders.deliverySlot', {
    date: formatDate.format(start),
    startTime: formatTime.format(start),
    endTime: formatTime.format(end),
  });
}

function isPdfCheck(checkPath: string): boolean {
  return checkPath.toLowerCase().endsWith('.pdf');
}

type DateAvailability = {
  managerId: number | null;
  slots: AvailableSlot[];
  loading: boolean;
};

interface BookingSuccess {
  orderId: number;
  date: string;
  startTime: string;
  endTime: string;
}

const DELIVERY_DAYS_COUNT = 7;

function resolveTimezone(timezone: string | null | undefined): string {
  if (timezone && moment.tz.zone(timezone)) {
    return timezone;
  }

  return moment.tz.guess();
}

function buildFutureDates(storeTimezone: string): string[] {
  const today = moment.tz(storeTimezone).startOf('day');
  return Array.from({ length: DELIVERY_DAYS_COUNT }, (_, index) =>
    today.clone().add(index, 'days').format('YYYY-MM-DD'),
  );
}

function formatDateTitle(value: string, storeTimezone: string, t: TFunction): string {
  const today = moment.tz(storeTimezone).format('YYYY-MM-DD');
  const tomorrow = moment.tz(storeTimezone).add(1, 'day').format('YYYY-MM-DD');

  if (value === today) {
    return t('delivery.today');
  }
  if (value === tomorrow) {
    return t('delivery.tomorrow');
  }

  return moment.tz(`${value}T00:00:00`, storeTimezone).format('ddd');
}

function formatDateNumber(value: string): string {
  const [, month, day] = value.split('-');
  if (!month || !day) {
    return value;
  }

  return `${day}/${month}`;
}

function parseOpenDeliveryOrderId(state: unknown): number | null {
  if (!state || typeof state !== 'object') {
    return null;
  }

  const candidate = (state as { openDelivery?: unknown }).openDelivery;
  if (typeof candidate === 'number' && Number.isInteger(candidate) && candidate > 0) {
    return candidate;
  }

  return null;
}

export default function OrdersScreen() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    sessionUser,
    products,
    appConfig,
    ordersLoading,
    orders,
    updatingOrderId,
    uploadingOrderId,
    clearError,
    setError,
    refreshUserOrders,
    cancelUserOrder,
    uploadOrderPaymentCheck,
  } = useWebsiteAppContext();
  const [showPastOrders, setShowPastOrders] = useState(false);
  const [itemsExpandedByOrder, setItemsExpandedByOrder] = useState<Record<number, boolean>>({});
  const [addressExpandedByOrder, setAddressExpandedByOrder] = useState<Record<number, boolean>>({});
  const [messageOpenByOrder, setMessageOpenByOrder] = useState<Record<number, boolean>>({});
  const [messageTextByOrder, setMessageTextByOrder] = useState<Record<number, string>>({});
  const [messageSendingOrderId, setMessageSendingOrderId] = useState<number | null>(null);
  const [selectedDeliveryOrderId, setSelectedDeliveryOrderId] = useState<number | null>(null);
  const [availabilityByDate, setAvailabilityByDate] = useState<Record<string, DateAvailability>>(
    {},
  );
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);
  const [deliveryLoading, setDeliveryLoading] = useState(false);
  const [deliveryBooking, setDeliveryBooking] = useState(false);
  const [deliveryError, setDeliveryError] = useState<string | null>(null);
  const [bookingSuccess, setBookingSuccess] = useState<BookingSuccess | null>(null);
  const storeTimezone = useMemo(() => resolveTimezone(appConfig?.timezone), [appConfig?.timezone]);
  const dateOptions = useMemo(() => buildFutureDates(storeTimezone), [storeTimezone]);

  const productImageById = useMemo(() => {
    const map = new Map<number, string | null>();
    products.forEach((product) => {
      map.set(product.id, product.images[0]?.url ?? null);
    });
    return map;
  }, [products]);

  const activeOrders = useMemo(
    () =>
      orders.filter(
        (order) =>
          ACTIVE_ORDER_STATUSES.includes(order.status) ||
          !PAST_ORDER_STATUSES.includes(order.status),
      ),
    [orders],
  );
  const pastOrders = useMemo(
    () => orders.filter((order) => PAST_ORDER_STATUSES.includes(order.status)),
    [orders],
  );
  const selectedDeliveryOrder = useMemo(
    () => orders.find((order) => order.id === selectedDeliveryOrderId) ?? null,
    [orders, selectedDeliveryOrderId],
  );

  function isItemsExpanded(orderId: number): boolean {
    return itemsExpandedByOrder[orderId] ?? true;
  }

  function isAddressExpanded(orderId: number): boolean {
    return addressExpandedByOrder[orderId] ?? true;
  }

  function isMessageOpen(orderId: number): boolean {
    return messageOpenByOrder[orderId] ?? false;
  }

  function getMessageText(orderId: number): string {
    return messageTextByOrder[orderId] ?? '';
  }

  function toggleItems(orderId: number): void {
    setItemsExpandedByOrder((current) => ({
      ...current,
      [orderId]: !(current[orderId] ?? true),
    }));
  }

  function toggleAddress(orderId: number): void {
    setAddressExpandedByOrder((current) => ({
      ...current,
      [orderId]: !(current[orderId] ?? true),
    }));
  }

  function toggleMessage(orderId: number): void {
    setMessageOpenByOrder((current) => ({
      ...current,
      [orderId]: !(current[orderId] ?? false),
    }));
  }

  function updateMessage(orderId: number, value: string): void {
    setMessageTextByOrder((current) => ({
      ...current,
      [orderId]: value,
    }));
  }

  async function handleSendMessage(orderId: number): Promise<void> {
    const message = getMessageText(orderId).trim();
    if (!message) {
      return;
    }

    clearError();
    setMessageSendingOrderId(orderId);

    try {
      await sendSupportMessage({ orderId, message });
      setMessageTextByOrder((current) => ({
        ...current,
        [orderId]: '',
      }));
      setMessageOpenByOrder((current) => ({
        ...current,
        [orderId]: false,
      }));
    } catch (requestError) {
      setError(
        getLocalizedApiErrorMessage(requestError, t, {
          exists: i18n.exists.bind(i18n),
        }),
      );
    } finally {
      setMessageSendingOrderId(null);
    }
  }

  async function handleCancelOrder(orderId: number): Promise<void> {
    if (!window.confirm(t('orderRow.confirmCancel'))) {
      return;
    }

    await cancelUserOrder(orderId);
  }

  useEffect(() => {
    if (!selectedDeliveryOrderId) {
      return;
    }

    let active = true;
    setDeliveryError(null);
    setSelectedSlot(null);
    setDeliveryLoading(true);
    setAvailabilityByDate(
      Object.fromEntries(
        dateOptions.map((date) => [date, { managerId: null, slots: [], loading: true }]),
      ),
    );

    const preload = async () => {
      const nextAvailability: Record<string, DateAvailability> = {};
      let firstAvailableDate: string | null = null;

      for (const date of dateOptions) {
        try {
          const availability = await fetchAvailableSlots(selectedDeliveryOrderId, date);
          nextAvailability[date] = { ...availability, loading: false };
          if (!firstAvailableDate && availability.slots.length > 0) {
            firstAvailableDate = date;
          }
        } catch {
          nextAvailability[date] = { managerId: null, slots: [], loading: false };
        }
      }

      if (!active) {
        return;
      }

      setAvailabilityByDate(nextAvailability);
      setSelectedDate(firstAvailableDate ?? dateOptions[0] ?? null);
      setDeliveryLoading(false);
    };

    void preload();

    return () => {
      active = false;
    };
  }, [dateOptions, selectedDeliveryOrderId]);

  useEffect(() => {
    setSelectedSlot(null);
  }, [selectedDate]);

  const openDeliverySelection = useCallback(
    (orderId: number): void => {
      clearError();
      setDeliveryError(null);
      setBookingSuccess(null);
      setSelectedDate(null);
      setSelectedSlot(null);
      setSelectedDeliveryOrderId(orderId);
    },
    [clearError],
  );

  useEffect(() => {
    const openDeliveryOrderId = parseOpenDeliveryOrderId(location.state);
    if (!openDeliveryOrderId) {
      return;
    }

    openDeliverySelection(openDeliveryOrderId);
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate, openDeliverySelection]);

  function closeDeliverySelection(forceClose = false): void {
    if (!forceClose && deliveryBooking) {
      return;
    }

    setSelectedDeliveryOrderId(null);
    setSelectedDate(null);
    setSelectedSlot(null);
    setDeliveryError(null);
    setDeliveryLoading(false);
    setAvailabilityByDate({});
  }

  const selectedAvailability = selectedDate ? availabilityByDate[selectedDate] : null;
  const availableSlots = selectedAvailability?.slots ?? [];

  async function handleConfirmDeliveryBooking(): Promise<void> {
    if (!selectedDeliveryOrderId || !selectedDate || !selectedSlot) {
      return;
    }

    clearError();
    setDeliveryError(null);
    setDeliveryBooking(true);

    try {
      await bookDeliverySlot({
        orderId: selectedDeliveryOrderId,
        date: selectedDate,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
      });
      await refreshUserOrders();
      setBookingSuccess({
        orderId: selectedDeliveryOrderId,
        date: selectedDate,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
      });
      closeDeliverySelection(true);
    } catch (requestError) {
      setDeliveryError(
        getLocalizedApiErrorMessage(requestError, t, {
          exists: i18n.exists.bind(i18n),
          fallbackKey: 'delivery.noSlots',
        }),
      );
    } finally {
      setDeliveryBooking(false);
    }
  }

  if (!sessionUser) {
    return (
      <div className="bg-primary border border-border rounded-2xl p-6 md:p-8 max-w-xl">
        <p className="m-0 text-xs font-bold uppercase tracking-wider text-accent">
          {t('website.orders.accountDashboard')}
        </p>
        <h1 className="m-0 mt-3 text-3xl font-bold tracking-tight">{t('website.orders.title')}</h1>
        <p className="m-0 mt-3 text-text-secondary">{t('website.orders.loginPrompt')}</p>
        <Link
          to="/login"
          className="mt-5 border-0 rounded-full px-6 py-3 cursor-pointer bg-accent-gradient text-on-accent inline-flex font-semibold"
        >
          {t('website.orders.goToLogin')}
        </Link>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-8 pb-8">
      <header className="space-y-3">
        <p className="m-0 text-xs font-bold uppercase tracking-wider text-accent">
          {t('website.orders.accountDashboard')}
        </p>
        <h1 className="m-0 text-4xl font-bold tracking-tight text-text">
          {t('website.orders.title')}
        </h1>
        <p className="m-0 max-w-2xl text-base text-text-secondary">
          {t('website.orders.subtitle')}
        </p>
      </header>

      {ordersLoading ? (
        <p className="m-0 text-sm text-text-secondary">{t('orders.loading')}</p>
      ) : null}
      {!ordersLoading && orders.length === 0 ? (
        <p className="m-0 text-sm text-text-secondary">{t('orders.empty')}</p>
      ) : null}

      {!ordersLoading && activeOrders.length > 0 ? (
        <section className="space-y-5">
          <h3 className="m-0 text-2xl font-semibold text-text flex items-center gap-3">
            {t('website.orders.activeOrders')}
            <span className="bg-accent-soft text-on-accent-soft text-[11px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">
              {t('website.orders.activeBadge', { count: activeOrders.length })}
            </span>
          </h3>
          <div className="space-y-6">
            {activeOrders.map((order) => {
              const canUploadCheck = order.status === 'PENDING';
              const canCancel =
                order.status === 'PENDING' || order.status === 'CONFIRMED';
              const canSelectDelivery = order.status === 'CONFIRMED' && !order.deliverySlot;
              const locale = i18n.resolvedLanguage ?? i18n.language;
              const statusLabel = getStatusLabel(order.status, t);
              const deliverySlotLabel = getDeliverySlotLabel(order, appConfig?.timezone, locale, t);
              const messageText = getMessageText(order.id);
              const isSendingMessage = messageSendingOrderId === order.id;

              return (
                <article
                  key={order.id}
                  className="bg-primary border border-border rounded-2xl shadow-sm overflow-hidden"
                >
                  <div className="p-6 md:p-8 flex flex-wrap items-start justify-between gap-6 border-b border-border">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-text">{order.orderNumber}</span>
                        <span
                          className={`px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${getStatusBadgeClass(order.status)}`}
                        >
                          {statusLabel}
                        </span>
                      </div>
                      <span className="text-sm text-text-secondary">
                        {t('website.orders.placedOn', {
                          date: formatOrderDate(order.createdAt, locale),
                          paymentMethod: formatPaymentMethod(order.paymentMethod, t),
                        })}
                      </span>
                    </div>

                    <div className="text-right">
                      <span className="block text-[11px] font-bold uppercase tracking-wider text-text-secondary">
                        {t('orderRow.totalAmount')}
                      </span>
                      <span className="text-2xl font-bold text-accent">
                        {formatCurrency(order.total)}
                      </span>
                    </div>
                  </div>

                  <div className="p-6 md:p-8 grid md:grid-cols-2 gap-8 md:gap-12">
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="m-0 font-semibold text-text uppercase text-[11px] tracking-widest">
                          {t('orderRow.itemsTitle', { count: order.items.length })}
                        </h4>
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 cursor-pointer text-accent text-[11px] font-bold uppercase tracking-wider"
                          onClick={() => toggleItems(order.id)}
                        >
                          {isItemsExpanded(order.id)
                            ? t('website.orders.collapse')
                            : t('website.orders.expand')}
                        </button>
                      </div>
                      {isItemsExpanded(order.id) ? (
                        <div className="space-y-4">
                          {order.items.map((item) => {
                            const productImageUrl =
                              item.productId !== null ? productImageById.get(item.productId) : null;
                            return (
                              <div key={item.id} className="flex gap-4 items-center">
                                <div className="w-16 h-16 rounded-2xl bg-secondary shrink-0 overflow-hidden border border-border">
                                  {productImageUrl ? (
                                    <img
                                      src={productImageUrl}
                                      alt={item.productName}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-text-secondary">
                                      <Icon name="image" />
                                    </div>
                                  )}
                                </div>
                                <div className="grow min-w-0">
                                  <p className="m-0 text-sm font-semibold text-text">
                                    {item.productName}
                                    {item.weight ? ` (${item.weight} ${item.unit})` : ''}
                                  </p>
                                  <p className="m-0 mt-0.5 text-xs text-text-secondary">
                                    {t('website.orders.quantity', { count: item.quantity })}
                                    {item.manufacturerName ? ` • ${item.manufacturerName}` : ''}
                                  </p>
                                </div>
                                <span className="text-sm font-semibold text-text">
                                  {formatCurrency(item.unitPrice * item.quantity)}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="m-0 font-semibold text-text uppercase text-[11px] tracking-widest">
                          {t('orderRow.deliveryAddress')}
                        </h4>
                        <button
                          type="button"
                          className="border-0 bg-transparent p-0 cursor-pointer text-accent text-[11px] font-bold uppercase tracking-wider"
                          onClick={() => toggleAddress(order.id)}
                        >
                          {isAddressExpanded(order.id)
                            ? t('website.orders.collapse')
                            : t('website.orders.expand')}
                        </button>
                      </div>

                      {isAddressExpanded(order.id) ? (
                        <div className="bg-secondary p-5 rounded-2xl border border-border">
                          <p className="m-0 text-sm text-text leading-relaxed">
                            {order.deliveryAddress}
                          </p>
                          {order.phoneNumber ? (
                            <p className="m-0 mt-2 text-sm text-text-secondary">
                              {order.phoneNumber}
                            </p>
                          ) : null}
                          {deliverySlotLabel ? (
                            <p className="m-0 mt-4 flex items-center gap-2 text-[12px] font-bold text-accent">
                              <Icon name="schedule" className="text-base" />
                              {deliverySlotLabel}
                            </p>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="bg-secondary p-6 md:px-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div className="flex flex-wrap gap-3">
                      {canUploadCheck ? (
                        <label className="bg-accent-gradient text-on-accent px-6 py-2.5 rounded-full font-bold text-sm flex items-center gap-2 cursor-pointer shadow-ambient">
                          <input
                            type="file"
                            className="hidden"
                            accept="image/*,.pdf,application/pdf"
                            onChange={(event) =>
                              void uploadOrderPaymentCheck(order.id, event.currentTarget.files)
                            }
                            disabled={uploadingOrderId === order.id}
                          />
                          <Icon name="attach_file" className="text-[20px]" />
                          {uploadingOrderId === order.id
                            ? t('website.orders.uploading')
                            : t('orderRow.attachCheck')}
                        </label>
                      ) : null}

                      {canSelectDelivery ? (
                        <button
                          type="button"
                          className="border-0 bg-accent-gradient text-on-accent px-6 py-2.5 rounded-full font-bold text-sm flex items-center gap-2 cursor-pointer shadow-ambient"
                          onClick={() => openDeliverySelection(order.id)}
                        >
                          <Icon name="schedule" className="text-[20px]" />
                          {t('delivery.selectTime')}
                        </button>
                      ) : null}

                      <button
                        type="button"
                        className="bg-primary text-text-secondary px-6 py-2.5 rounded-full font-bold text-sm border border-border hover:bg-secondary/70 cursor-pointer"
                        onClick={() => toggleMessage(order.id)}
                      >
                        {t('orderRow.messageSupport')}
                      </button>
                    </div>

                    {canCancel ? (
                      <button
                        type="button"
                        className="border-0 bg-transparent text-error font-bold text-sm uppercase tracking-wider cursor-pointer hover:underline disabled:opacity-50"
                        disabled={updatingOrderId === order.id}
                        onClick={() => void handleCancelOrder(order.id)}
                      >
                        {t('orderRow.cancelOrder')}
                      </button>
                    ) : null}
                  </div>

                  {isMessageOpen(order.id) ? (
                    <div className="px-6 md:px-8 pb-6 bg-secondary/50">
                      <div className="bg-primary border border-border rounded-2xl p-4 flex flex-col gap-3">
                        <textarea
                          className="w-full text-sm p-3 rounded-xl border border-border-solid bg-primary text-text resize-none focus:outline-none focus:ring-2 focus:ring-accent/20"
                          value={messageText}
                          onChange={(event) => updateMessage(order.id, event.currentTarget.value)}
                          placeholder={t('orderRow.messagePlaceholder')}
                          rows={3}
                        />
                        <div className="flex justify-end">
                          <button
                            type="button"
                            className="bg-accent text-on-accent border-0 py-2 px-5 rounded-full font-semibold cursor-pointer disabled:opacity-50"
                            onClick={() => void handleSendMessage(order.id)}
                            disabled={isSendingMessage || !messageText.trim()}
                          >
                            {isSendingMessage ? t('common.sending') : t('common.send')}
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {!ordersLoading && pastOrders.length > 0 ? (
        <section className="space-y-4">
          <button
            type="button"
            className="w-full border-0 p-0 bg-transparent cursor-pointer flex items-center justify-between"
            onClick={() => setShowPastOrders((current) => !current)}
          >
            <h3 className="m-0 text-2xl font-semibold text-text">{t('orders.past')}</h3>
            <div className="flex items-center gap-2 text-text-secondary">
              <span className="text-xs font-bold uppercase tracking-wider">
                {showPastOrders ? t('common.hide') : t('common.show')}
              </span>
              <Icon
                name={showPastOrders ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
                className="text-base"
              />
            </div>
          </button>

          {showPastOrders ? (
            <div className="space-y-3">
              {pastOrders.map((order) => {
                const firstCheck = order.paymentChecks[0] ?? null;
                const hasPdfCheck = firstCheck ? isPdfCheck(firstCheck.path) : false;
                return (
                  <article
                    key={order.id}
                    className="bg-secondary border border-border rounded-2xl px-5 py-4 flex items-center justify-between gap-4"
                  >
                    <div className="flex items-center gap-4 min-w-0">
                      <div className="w-11 h-11 rounded-2xl bg-primary border border-border overflow-hidden shrink-0">
                        {firstCheck ? (
                          hasPdfCheck ? (
                            <div className="w-full h-full flex items-center justify-center text-error">
                              <Icon name="picture_as_pdf" />
                            </div>
                          ) : (
                            <img
                              src={firstCheck.url}
                              alt={t('website.orders.paymentCheckAlt', {
                                orderNumber: order.orderNumber,
                              })}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          )
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-text-secondary">
                            <Icon name="receipt" />
                          </div>
                        )}
                      </div>

                      <div className="min-w-0">
                        <p className="m-0 text-sm font-bold text-text">{order.orderNumber}</p>
                        <p className="m-0 mt-0.5 text-xs text-text-secondary">
                          {formatOrderDate(order.createdAt, i18n.resolvedLanguage ?? i18n.language)}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                      <span
                        className={`hidden md:inline-flex px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${getStatusBadgeClass(order.status)}`}
                      >
                        {getStatusLabel(order.status, t)}
                      </span>
                      <p className="m-0 text-sm font-bold text-text">
                        {formatCurrency(order.total)}
                      </p>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : null}
        </section>
      ) : null}

      {selectedDeliveryOrderId ? (
        <div className="fixed inset-0 z-30 bg-scrim px-4 py-8 overflow-y-auto">
          <div className="min-h-full flex items-center justify-center">
            <div className="w-full max-w-2xl bg-primary rounded-2xl border border-border shadow-ambient overflow-hidden">
              <div className="px-6 py-5 border-b border-border flex items-start justify-between gap-4">
                <div>
                  <h3 className="m-0 text-xl font-semibold text-text">
                    {t('delivery.selectTime')}
                  </h3>
                  <p className="m-0 mt-1 text-sm text-text-secondary">
                    {selectedDeliveryOrder
                      ? t('website.orders.chooseSlotForOrder', {
                          orderNumber: selectedDeliveryOrder.orderNumber,
                        })
                      : t('website.orders.chooseAvailableSlot')}
                  </p>
                </div>
                <button
                  type="button"
                  className="border-0 bg-transparent text-text-secondary cursor-pointer hover:text-text"
                  onClick={() => closeDeliverySelection()}
                  disabled={deliveryBooking}
                  aria-label={t('website.orders.closeDeliveryScheduling')}
                >
                  <Icon name="close" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {deliveryError ? <p className="m-0 text-sm text-error">{deliveryError}</p> : null}

                <div className="flex gap-2 overflow-x-auto pb-1">
                  {dateOptions.map((date) => {
                    const availability = availabilityByDate[date];
                    const hasSlots = (availability?.slots.length ?? 0) > 0;
                    const isLoadingDay = availability?.loading ?? deliveryLoading;
                    const isSelected = selectedDate === date;
                    const isDisabled = !isLoadingDay && !hasSlots;

                    return (
                      <button
                        key={date}
                        type="button"
                        className={`min-w-[86px] rounded-4xl border border-border p-2.5 grid gap-0.5 justify-items-center cursor-pointer transition-colors ${isSelected ? 'bg-accent-gradient text-on-accent' : 'bg-surface-high text-text'} ${isDisabled ? 'opacity-45 cursor-not-allowed' : ''}`}
                        onClick={() => setSelectedDate(date)}
                        disabled={isDisabled}
                      >
                        <span className="text-[0.75rem]">
                          {formatDateTitle(date, storeTimezone, t)}
                        </span>
                        <strong className="text-[0.875rem]">{formatDateNumber(date)}</strong>
                      </button>
                    );
                  })}
                </div>

                <div className="grid grid-cols-2 gap-2.5">
                  {deliveryLoading ? (
                    <p className="my-3 text-text-secondary text-center col-span-2">
                      {t('website.orders.loadingAvailableSlots')}
                    </p>
                  ) : null}
                  {!deliveryLoading && availableSlots.length === 0 ? (
                    <p className="my-3 text-text-secondary text-center col-span-2">
                      {t('website.orders.noAvailableSlotsForDay')}
                    </p>
                  ) : null}
                  {!deliveryLoading
                    ? availableSlots.map((slot) => {
                        const slotKey = `${slot.startTime}-${slot.endTime}`;
                        const isSelected =
                          selectedSlot?.startTime === slot.startTime &&
                          selectedSlot?.endTime === slot.endTime;

                        return (
                          <button
                            key={slotKey}
                            type="button"
                            className={`rounded-4xl border border-border p-[11px] text-[0.875rem] cursor-pointer transition-colors ${isSelected ? 'bg-accent-gradient text-on-accent' : 'bg-secondary text-text'}`}
                            onClick={() => setSelectedSlot(slot)}
                          >
                            {slot.startTime} - {slot.endTime}
                          </button>
                        );
                      })
                    : null}
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    className="border border-border bg-primary text-text-secondary px-5 py-2.5 rounded-full font-semibold cursor-pointer disabled:opacity-50"
                    onClick={() => closeDeliverySelection()}
                    disabled={deliveryBooking}
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="button"
                    className="border-0 bg-accent-gradient text-on-accent px-5 py-2.5 rounded-full font-semibold cursor-pointer disabled:opacity-70 disabled:cursor-not-allowed"
                    disabled={!selectedSlot || deliveryBooking}
                    onClick={() => void handleConfirmDeliveryBooking()}
                  >
                    {deliveryBooking ? t('website.orders.booking') : t('delivery.confirmTime')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {bookingSuccess ? (
        <div className="fixed inset-0 z-30 bg-scrim grid place-items-center p-4">
          <div className="w-[min(420px,100%)] bg-primary rounded-2xl border border-border shadow-ambient p-5 text-center grid gap-2.5">
            <div className="w-11 h-11 rounded-circle mx-auto grid place-items-center bg-success-container text-on-success-container text-xl font-bold">
              ✓
            </div>
            <h3 className="m-0 text-lg font-bold">{t('delivery.scheduled')}</h3>
            <p className="m-0 text-sm text-text-secondary">
              {t('website.orders.bookingSuccess', {
                orderId: bookingSuccess.orderId,
                date: moment
                  .tz(`${bookingSuccess.date}T00:00:00`, storeTimezone)
                  .format('DD/MM/YYYY'),
                startTime: bookingSuccess.startTime,
                endTime: bookingSuccess.endTime,
              })}
            </p>
            <button
              type="button"
              className="rounded-full px-6 py-3 bg-accent-gradient text-on-accent font-semibold border-none cursor-pointer mt-2"
              onClick={() => setBookingSuccess(null)}
            >
              {t('website.orders.backToOrders')}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
